// The job list.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { scheduling, type JobListItem, type JobStatus } from '../schedulingApi';
import { ApiError } from '../api';
import { formatDate } from '../lib/dates';
import { Banner } from '../ui';
import { EmptyState, PageHeader } from '../components/ui/Page';
import { StatusBadge } from '../components/ui/StatusBadge';
import { CardList, RowCard } from '../components/ui/RowCard';
import { LinkedRow } from '../components/ui/LinkedRow';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { useLatestRequest } from '../lib/useLatestRequest';

type SortKey = 'number' | 'title' | 'client' | 'status' | 'nextBooked' | 'site';

/**
 * Every status that may be DISPLAYED, in ladder order.
 *
 * This object is also the tab strip: the list builds its tabs from these keys,
 * so a new status becomes a new tab by being added here and nowhere else.
 * Invoiced sits after Completed because that is where it sits in the ladder —
 * the last thing that happens to a job.
 */
export const STATUS_LABEL: Record<JobStatus, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  // Round 45: its own tab, after the open work and before the closed.
  on_hold: 'On hold',
  completed: 'Completed',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
};

export function JobsPage() {
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Defaults to the number, descending — newest job first, which is the order
  // the server already sends within a status group and the one somebody
  // opening this page is looking for.
  const { sortKey, sortDir, onSort } = useSort<SortKey>('number', 'desc');

  // The status filter lives in the URL, like Zanetto Tools' job list: a
  // manager who has narrowed to "In progress" can bookmark it or send it to
  // somebody, and a refresh does not throw the filter away. Empty means all.
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const setStatus = (next: string) => {
    const updated = new URLSearchParams(params);
    if (next) updated.set('status', next);
    else updated.delete('status');
    setParams(updated, { replace: true });
  };

  const begin = useLatestRequest();

  const load = useCallback(async () => {
    // A search runs on every keystroke, so several are in flight at once and
    // only the newest answer may land. See `useLatestRequest`.
    const isCurrent = begin();
    try {
      const result = await scheduling.listJobs({
        status: status || undefined,
        search: search || undefined,
      });
      if (!isCurrent()) return;
      setJobs(result.jobs);
      setTotal(result.total);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the jobs.');
    }
  }, [begin, status, search]);

  useEffect(() => {
    void load();
  }, [load]);

  // One sorted array for both renderings. Note this sorts the rows the server
  // sent, which is capped at 200 — the note above the table already says so,
  // and a sort that silently reached past the cap would be a different
  // (server-side) feature wearing this one's clothes.
  const visible = useMemo(
    () =>
      sortRows(
        jobs,
        (job) => {
          switch (sortKey) {
            case 'number':
              return job.number;
            case 'title':
              return job.title;
            case 'client':
              return job.clientName;
            case 'status':
              return STATUS_LABEL[job.status];
            case 'nextBooked':
              return job.nextBookedDate;
            case 'site':
              return job.siteAddress;
          }
        },
        sortDir
      ),
    [jobs, sortKey, sortDir]
  );

  return (
    <>
      <PageHeader
        title="Jobs"
        actions={
          <Link to="/app/jobs/new" className="btn-primary">
            New job
          </Link>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}

      {/* The status filter as a tab strip rather than a dropdown (ZT's job
          list). A dropdown hides which statuses exist until it is opened;
          the strip shows the whole set and takes one tap. Same markup as
          the tabs on a person's page, so the app has one tab idiom. */}
      <div
        className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200"
        role="tablist"
        aria-label="Filter jobs by status"
      >
        {[['', 'All'] as const, ...Object.entries(STATUS_LABEL)].map(([value, label]) => {
          const active = status === value;
          return (
            <button
              key={value || 'all'}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStatus(value)}
              className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                active
                  ? 'border-brand-700 font-medium text-brand-800'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-80">
          <label className="form-label" htmlFor="job-search">
            Search
          </label>
          <input
            id="job-search"
            type="search"
            className="form-input"
            placeholder="Search by job number or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {jobs.length < total && (
        <p className="muted mb-3">
          Showing the first {jobs.length} of {total} jobs; open work sorts first. Narrow with
          search or the status filter to find older finished work.
        </p>
      )}

      {jobs.length === 0 ? (
        <EmptyState
          title="No jobs yet"
          body="Create one and it will appear here, with everything booked against it."
        />
      ) : (
        <>
        <CardList>
          {visible.map((job) => (
            <RowCard
              key={job.id}
              to={`/app/jobs/${job.id}`}
              title={
                <>
                  <span className="tabular-nums">{job.number}</span> · {job.title}
                </>
              }
              badges={<StatusBadge status={job.status} kind="job" />}
            >
              {job.conflictCount > 0 && (
                <div className="font-medium text-red-700">needs re-planning</div>
              )}
              {job.clientName && <div>{job.clientName}</div>}
              <div>
                {job.nextBookedDate ? `Next booked ${formatDate(job.nextBookedDate)}` : 'Not booked'}
              </div>
              {job.siteAddress && <div>{job.siteAddress}</div>}
            </RowCard>
          ))}
        </CardList>
        <div className="table-wrap max-sm:hidden">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader label="Number" sortKey="number" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Title" sortKey="title" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Client" sortKey="client" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Next booked" sortKey="nextBooked" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Site" sortKey="site" current={sortKey} dir={sortDir} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {visible.map((job) => (
                <LinkedRow key={job.id} to={`/app/jobs/${job.id}`}>
                  <td className="whitespace-nowrap font-medium tabular-nums">
                    <Link to={`/app/jobs/${job.id}`} className="table-link">
                      {job.number}
                    </Link>
                  </td>
                  <td>
                    {job.title}
                    {job.conflictCount > 0 && (
                      <span className="pill conflict-pill ml-2">needs re-planning</span>
                    )}
                  </td>
                  <td className="table-muted">{job.clientName ?? '—'}</td>
                  <td>
                    {/* The status is the column a manager scans down, so it is a
                        badge rather than the grey pill every other value used to
                        share — five statuses that all looked identical told them
                        nothing at a glance. */}
                    <StatusBadge status={job.status} kind="job" />
                  </td>
                  <td className="table-muted whitespace-nowrap">
                    {job.nextBookedDate ? formatDate(job.nextBookedDate) : 'not booked'}
                  </td>
                  <td className="table-muted">{job.siteAddress}</td>
                </LinkedRow>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </>
  );
}
